import React, { useState, useEffect, useMemo, useRef } from 'react'
import injectStyles from 'react-jss'
import cn from 'classnames'

import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import InputAdornment from '@material-ui/core/InputAdornment'

import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'

import styles from '../CityMetricTable/styles.js'
import {
  calculateTotalCostDependingOfType,
  generateTotalRow,
  getComparator,
  isValueValid,
  roundTo2,
  stableSort,
} from '../AffiliateMetricTable/utils.js'
import { Select } from '@material-ui/core'
import { MenuItem } from 'material-ui'
import DeleteIcon from '../../Assets/Images/icons/deleteIcon'
import { theme } from 'Assets/Styles/theme'
import StyledIconButton from '../Form/StyledIconButton'
import { handleConfirmationDialog } from '../../Containers/City/Page/Ambasadors/EmailPipelineView/utils'
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog'
import { AFFILIATE_METRICS_HEADERS, INSERTS_PACK_SIZE } from './option.js'

const AffiliateMetricTable = ({
  classes,
  currencySign = '$',
  campaignData,
  affiliateClients,
  affiliateMetricsValues,
  setAffiliateMetricsValues,
  disabled = false,
}) => {
  const [totalRow, setTotalRow] = useState([])
  const [focusedCell, setFocusedCell] = useState({ rowId: null, key: null })
  const [confirmDialogData, setConfirmDialogData] = useState({ isOpen: false })
  const [
    localAffiliateMetricsValues,
    setLocalAffiliateMetricsValues,
  ] = useState(affiliateMetricsValues)
  const prevAffiliateMetricsValues = useRef(affiliateMetricsValues)
  const isSelfServe = useMemo(
    () =>
      campaignData &&
      campaignData.campaign &&
      campaignData.campaign.isSelfServe,
    [campaignData],
  )

  const selectedAffiliateIds = useMemo(() => {
    return localAffiliateMetricsValues.map(metric => Number(metric.clientId))
  }, [localAffiliateMetricsValues])

  const formatedAffiliateList = useMemo(() => {
    return affiliateClients
      .filter(affiliate => !selectedAffiliateIds.includes(Number(affiliate.id)))
      .map(affiliate => ({
        value: affiliate.id,
        label: affiliate.name,
      }))
  }, [affiliateClients, selectedAffiliateIds])

  const headers = AFFILIATE_METRICS_HEADERS

  useEffect(() => {
    if (
      JSON.stringify(affiliateMetricsValues) !==
      JSON.stringify(prevAffiliateMetricsValues.current)
    ) {
      setLocalAffiliateMetricsValues(affiliateMetricsValues)
      prevAffiliateMetricsValues.current = affiliateMetricsValues
    }
  }, [affiliateMetricsValues])

  useEffect(() => {
    const affiliateMetricsWithTotal = calculateTotalCostDependingOfType(
      localAffiliateMetricsValues,
    )
    const generatedTotalRow = generateTotalRow(affiliateMetricsWithTotal)
    setTotalRow(generatedTotalRow)
    setAffiliateMetricsValues(affiliateMetricsWithTotal)
  }, [localAffiliateMetricsValues, setAffiliateMetricsValues])

  const handleCellChange = (value, row, key, shouldRound = false) => {
    const newValue = value === '' || value <= 0 ? 0 : Number(value)
    const packSize = INSERTS_PACK_SIZE.DEFAULT
    //FOR FUTURE IMPLEMENTATION
    // INSERTS_PACK_SIZE[row.affiliatePackSizeType || 'DEFAULT'] ||

    const newAffiliateMetrics = affiliateMetricsValues.map(affiliate =>
      affiliate.id === row.id
        ? {
            ...affiliate,
            [key]:
              shouldRound && key === 'affiliateTotalInserts'
                ? Math.round(newValue / packSize) * packSize
                : newValue,
            ...(key === 'affiliateTotalInserts'
              ? {
                  affiliateTotalPacks: Math.floor(
                    shouldRound
                      ? Math.round(newValue / packSize)
                      : newValue / packSize,
                  ),
                }
              : {}),
          }
        : affiliate,
    )

    const affiliateMetricsWithTotal = calculateTotalCostDependingOfType(
      newAffiliateMetrics,
    )
    const generatedTotalRow = generateTotalRow(affiliateMetricsWithTotal)
    setTotalRow(generatedTotalRow)
    setLocalAffiliateMetricsValues(affiliateMetricsWithTotal)
    setAffiliateMetricsValues(affiliateMetricsWithTotal)
  }

  const handleInputChange = ({ target: { value } }, row, key) => {
    if (!isValueValid(value)) return

    const [integerPart = '', decimalPart = ''] = value.split(/[.,]/)
    if (integerPart.length > 6 || decimalPart.length > 2) return

    handleCellChange(value, row, key, false)
  }

  const renderTableCell = (row, key, type) => {
    const uniqKey = `${row.id}-${key}`
    switch (type) {
      case 'currencySignTotal':
        return (
          <Typography key={uniqKey}>
            {currencySign}
            {roundTo2(row[key])}
          </Typography>
        )
      case 'tax': {
        return <Typography key={uniqKey}>{row[key]}%</Typography>
      }
      case 'taxAmount': {
        return (
          <Typography key={uniqKey}>
            {currencySign}
            {roundTo2(row[key])}
          </Typography>
        )
      }
      case 'subtotal': {
        return (
          <Typography key={uniqKey}>
            {currencySign}
            {roundTo2(row[key])}
          </Typography>
        )
      }
      case 'dropdownCity':
        const selectedAffiliate = affiliateClients.find(
          affiliate => Number(affiliate.id) === Number(row.clientId),
        )

        const isValueInOptions =
          selectedAffiliate ||
          formatedAffiliateList.some(
            affiliate => Number(affiliate.value) === Number(row.clientId),
          )

        const selectValue = row.clientName
          ? row.clientId
          : isValueInOptions
          ? row.clientId
          : ''

        const filteredAffiliateList = formatedAffiliateList.filter(
          affiliate => Number(affiliate.value) !== Number(row.clientId),
        )

        return (
          <Select
            key={uniqKey}
            className={classes.select}
            value={selectValue || ''}
            onChange={event => {
              const newValue = event.target.value

              if (newValue === '') {
                setLocalAffiliateMetricsValues(prevValues => {
                  return prevValues.map(affiliate => {
                    return affiliate.id === row.id
                      ? {
                          ...affiliate,
                          [key]: null,
                          clientId: null,
                          clientName: null,
                        }
                      : affiliate
                  })
                })

                setTimeout(() => {
                  handleCellChange(null, row, key)
                }, 0)

                return
              }

              setLocalAffiliateMetricsValues(prevValues => {
                return prevValues.map(affiliate => {
                  return affiliate.id === row.id
                    ? {
                        ...affiliate,
                        [key]: Number(newValue) === 0 ? null : Number(newValue),
                      }
                    : affiliate
                })
              })

              setTimeout(() => {
                handleCellChange(newValue, row, key)
              }, 0)
            }}
            disableUnderline={true}
            disabled={disabled}
            //MenuProps={{
            //  PaperProps: {
            //    style: {
            //      maxHeight: 300,
            //    },
            //  },
            //}}
          >
            <MenuItem value="">Select Affiliate</MenuItem>
            {selectedAffiliate && row.clientId ? (
              <MenuItem value={selectedAffiliate.id}>
                {selectedAffiliate.name}
              </MenuItem>
            ) : row.clientName && row.clientId ? (
              <MenuItem value={row.clientId}>{row.clientName}</MenuItem>
            ) : null}
            {filteredAffiliateList.map(affiliate => (
              <MenuItem key={affiliate.value} value={affiliate.value}>
                {affiliate.label}
              </MenuItem>
            ))}
          </Select>
        )
      case 'currencySign':
        return (
          <OutlinedInput
            key={uniqKey}
            id={uniqKey}
            type="number"
            value={
              focusedCell.rowId === row.id &&
              focusedCell.key === key &&
              row[key] === 0
                ? ''
                : row[key]
            }
            sx={{
              '& .notchedOutline': {
                border: 'none',
              },
            }}
            onFocus={() => setFocusedCell({ rowId: row.id, key })}
            onBlur={event => {
              if (event.target.value === '') {
                handleCellChange('0', row, key, true)
              }
              setFocusedCell({ rowId: null, key: null })
            }}
            onChange={event => handleInputChange(event, row, key)}
            startAdornment={
              <InputAdornment position="start">{currencySign}</InputAdornment>
            }
            classes={{
              input: classes.outlinedInput,
              root: classes.outlinedInputRoot,
            }}
            disabled={disabled}
          />
        )
      case 'number':
        return (
          <OutlinedInput
            key={uniqKey}
            id={uniqKey}
            value={
              focusedCell.rowId === row.id &&
              focusedCell.key === key &&
              row[key] === 0
                ? ''
                : row[key]
            }
            type="number"
            onFocus={() => setFocusedCell({ rowId: row.id, key })}
            onBlur={event => {
              if (event.target.value === '') {
                handleCellChange('0', row, key, true)
              } else if (key === 'affiliateTotalInserts') {
                handleCellChange(event.target.value, row, key, true)
              }
              setFocusedCell({ rowId: null, key: null })
            }}
            onChange={event => handleInputChange(event, row, key)}
            classes={{
              input: classes.outlinedNumberInput,
              root: classes.outlinedInputRoot,
            }}
            disabled={disabled}
          />
        )
      case 'delete': {
        return !disabled ? (
          <div
            key={row.id}
            className={classes.deleteButtonContainer}
            onClick={() => deleteAffiliate(row.id)}
          >
            <div className={classes.buttonTitle}>
              <DeleteIcon color={theme.colors.primary} />
            </div>
          </div>
        ) : null
      }

      default:
        return <Typography key={uniqKey}>{row[key]}</Typography>
    }
  }

  const TotalRow = ({ totalRow }) => {
    return (
      <TableRow hover={false} tabIndex={-1}>
        {totalRow.map((item, index) => {
          const showCurrencySign = ![
            'affiliateTotalInserts',
            'affiliateTotalPacks',
            'tax',
          ].includes(item.key)
          return (
            <TableCell
              align={index === 0 ? 'left' : 'center'}
              className={
                index !== 0
                  ? classes.tableHeaderCell
                  : classes.firstTableHeaderCell
              }
            >
              {item.label ? (
                <Typography className={classes.tableHeadersLabel}>
                  {item.label}
                </Typography>
              ) : item.value ? (
                <Typography className={classes.tableHeadersLabel}>
                  {showCurrencySign && currencySign}
                  {item.value}
                </Typography>
              ) : (
                <Typography className={classes.tableHeadersLabel}>
                  {'-'}
                </Typography>
              )}
            </TableCell>
          )
        })}
      </TableRow>
    )
  }

  useEffect(() => {
    const affiliateMetricsWithTotal = calculateTotalCostDependingOfType(
      affiliateMetricsValues,
    )
    const generatedTotalRow = generateTotalRow(affiliateMetricsWithTotal)
    setTotalRow(generatedTotalRow)
    setAffiliateMetricsValues(affiliateMetricsWithTotal)
  }, [setAffiliateMetricsValues])

  const handleSelfServeAddAffiliate = () => {
    handleConfirmationDialog({
      title: 'Restricted!',
      contentText: `You cannot manually add affiliate metrics for self-serve campaigns. If you need to do this, please contact tech.`,
      setConfirmDialogData,
      rightButtonLable: 'OK',
    })
  }

  const addAffiliate = () => {
    const newAffiliateId = `newAffiliate-${Date.now()}`
    const initialTotalInserts = 0
    const packSize = INSERTS_PACK_SIZE.DEFAULT

    const newAffiliateMetrics = {
      id: `newAffiliate-${newAffiliateId}`,
      clientId: null,
      // TYPE OF PACK SIZE FOR FUTURE CONFIGURATION
      // affiliatePackSizeType: 'DEFAULT',
      affiliateTotalInserts: initialTotalInserts,
      affiliateTotalPacks: Math.floor(initialTotalInserts / packSize),
      affiliateInsertCost: 0,
      affiliatePromofyFee: 0,
      affiliateFreightCost: 0,
      affiliatePrintingCost: 0,
      affiliateBonusBudget: 0,
    }

    const newMetrics = [...localAffiliateMetricsValues, newAffiliateMetrics]
    setLocalAffiliateMetricsValues(newMetrics)
    setAffiliateMetricsValues(newMetrics)
  }

  const deleteAffiliate = id => {
    const newMetrics = localAffiliateMetricsValues.filter(
      item => item.id !== id,
    )
    setLocalAffiliateMetricsValues(newMetrics)
    setAffiliateMetricsValues(newMetrics)

    const affiliateMetricsWithTotal = calculateTotalCostDependingOfType(
      newMetrics,
    )
    const generatedTotalRow = generateTotalRow(affiliateMetricsWithTotal)
    setTotalRow(generatedTotalRow)
  }

  return (
    <Box className={classes.wrapper}>
      {confirmDialogData.isOpen && <ConfirmDialog {...confirmDialogData} />}
      <Box className={classes.table}>
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader size="medium">
            <TableHead className={classes.tableHead}>
              <TableRow className={classes.tableRow}>
                {headers.map((headCell, index) => (
                  <TableCell
                    key={headCell.key}
                    align={index === 0 ? 'left' : 'center'}
                    className={
                      index !== 0
                        ? classes.tableHeaderCell
                        : classes.firstTableHeaderCell
                    }
                  >
                    <Typography className={classes.tableHeadersLabel}>
                      {headCell.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {stableSort(
                localAffiliateMetricsValues,
                getComparator('abs', 'id'),
              ).map(row => {
                return (
                  <TableRow hover={false} tabIndex={-1} key={row.id}>
                    {headers.map(({ key, type }, index) => {
                      return (
                        <TableCell
                          key={`${row.id}-${key}-${index}`}
                          align={index === 0 ? 'left' : 'center'}
                          className={cn(type ? classes.padding10 : {})}
                        >
                          {renderTableCell(row, key, type, affiliateClients)}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
              <TotalRow totalRow={totalRow} />
            </TableBody>
            {!disabled ? (
              <div
                className={classes.buttonContainer}
                onClick={
                  isSelfServe ? handleSelfServeAddAffiliate : addAffiliate
                }
              >
                <StyledIconButton
                  className={classes.addButton}
                  label="Add Affiliate"
                >
                  +
                </StyledIconButton>
                <span className={classes.buttonLabel}>Add Affiliate</span>
              </div>
            ) : null}
          </Table>
        </TableContainer>
      </Box>
    </Box>
  )
}

export default injectStyles(styles)(AffiliateMetricTable)
